import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../components/utils/TableTimeStamps';
import Copyable from '../../../components/utils/Copyable';
import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import {
  GetDemoAnalyticsWT
} from '../../../redux/actions/watchTower/certifyDemoAnalyticsActions';

// bright accents that read well on the app's dark surfaces
const DISPOSITION_CHIP = {
  allowlisted: { label: 'Allowlisted', accent: '#4ade80' },
  'pending-review': { label: 'Pending review', accent: '#fbbf24' },
  blocked: { label: 'Blocked', accent: '#f87171' },
};

// translucent tinted pill with matching light text
const chipSx = (accent) => ({
  backgroundColor: `${accent}22`,
  color: accent,
  border: `1px solid ${accent}55`,
  fontWeight: 600,
});

const TILE_LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

const DemoAnalyticsPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state.watchTower);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    total: 0,
    allowlisted: 0,
    pendingReview: 0,
    blocked: 0,
    viewed: 0,
  });

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "Name", sort: "name" },
    { label: "Email", sort: "email" },
    { label: "Requested at", sort: "createdAt" },
    { label: "Disposition", sort: null },
    { label: "Saw videos", sort: null },
  ];

  const summaryTiles = [
    { label: "Total requests", value: summary.total, accent: "#e2f2f9" },
    { label: "Allowlisted", value: summary.allowlisted, accent: "#4ade80" },
    { label: "Pending review", value: summary.pendingReview, accent: "#fbbf24" },
    { label: "Blocked", value: summary.blocked, accent: "#f87171" },
    { label: "Saw videos", value: summary.viewed, accent: "#60a5fa" },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetAnalytics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setList(state.demoCertifyAnalytics.records || []);
      setTotal(state.demoCertifyAnalytics.total || 0);
      if (state.demoCertifyAnalytics.summary) {
        setSummary(state.demoCertifyAnalytics.summary);
      }
    } catch (err) {}
  }, [state]);

  async function handleGetAnalytics() {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&field=${sort}`;
    if (sortDirection) query += `&order=${sortDirection}`;
    return dispatch(GetDemoAnalyticsWT(page + 1, rowsPerPage, query, setLoading));
  };

  const dispositionChip = (d) => {
    const c = DISPOSITION_CHIP[d] || { label: d || 'Unknown', accent: '#9ca3af' };
    return <Chip size="small" label={c.label} sx={chipSx(c.accent)} />;
  };

  const viewedChip = (viewed) => (
    viewed
      ? <Chip size="small" label="Saw videos" sx={chipSx('#60a5fa')} />
      : <Chip size="small" label="Not yet" sx={chipSx('#9ca3af')} />
  );

  return (
    <Box>
      <TopBar
        title={"Demo Access Analytics"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetAnalytics();
        }}
        />

      {/* ---------- summary tiles ---------- */}
      <Box
        display={"flex"}
        flexWrap={"wrap"}
        gap={"12px"}
        mb={"18px"}>
        {
          summaryTiles.map((tile, i) => (
            <Box
              key={i}
              sx={{
                flex: "1 1 150px",
                minWidth: "150px",
                border: `1px solid ${tile.accent}40`,
                background: `${tile.accent}14`,
                borderRadius: "12px",
                padding: "14px 16px",
              }}>
              <Typography
                sx={{ fontSize: "26px", fontWeight: 700, lineHeight: 1.1, color: tile.accent }}>
                {tile.value}
              </Typography>
              <Typography
                variant='caption'
                sx={{ color: TILE_LABEL_COLOR }}>
                {tile.label}
              </Typography>
            </Box>
          ))
        }
      </Box>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          list.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No demo requests found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                list.map((row, index) => (
                  <TableRow
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name || <NA />}</TableCell>
                    <TableCell>{row.email || <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.requestedAt}
                        />
                    </TableCell>
                    <TableCell>{dispositionChip(row.disposition)}</TableCell>
                    <TableCell>{viewedChip(row.viewed)}</TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default DemoAnalyticsPage;
